import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { ReactNode, useContext, useEffect, useState } from "react";
import {
  CARRIERS,
  CARRIER_LABELS,
  Carrier,
  LABEL_FORMATS,
  LabelFormat,
  PRINT_FORMATS,
  PrinterType,
  ShipmentSettingsType,
} from "types/ShippingTypes";
import {
  apiGetPrinters,
  apiGetShipmentSettings,
  apiSaveShipmentSettings,
} from "util/network/Shipping";
import { ShipmentPage, errorText, useIsManager } from "./common";

const Section: React.FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 2 }}>
      {title}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>{children}</Box>
  </Paper>
);

const printerKey = (hostName: string, printerName: string) =>
  `${hostName}\u0000${printerName}`;

/** The SHIPMENT setting, edited here instead of the generic settings editor:
 *  the label printer, carriers, sender, notifications and the checks. Saved as
 *  one object; the backend refuses unknown keys and checks every value. */
const ShipmentSettings: React.FC = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const isManager = useIsManager();
  const [settings, setSettings] = useState<ShipmentSettingsType>();
  const [printers, setPrinters] = useState<PrinterType[] | null>(null);
  const [printersError, setPrintersError] = useState<string | null>(null);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    apiGetShipmentSettings(token)
      .then(setSettings)
      .catch((error) => addToast({ type: "error", message: errorText(error) }));
  }, [token]);

  const loadPrinters = () => {
    setLoadingPrinters(true);
    setPrintersError(null);
    apiGetPrinters(token)
      .then(setPrinters)
      .catch((error) => setPrintersError(errorText(error)))
      .finally(() => setLoadingPrinters(false));
  };

  if (!settings) {
    return (
      <ShipmentPage title="Shipment settings">
        <CircularProgress />
      </ShipmentPage>
    );
  }

  const disabled = !isManager || saving;
  const set = <K extends keyof ShipmentSettingsType>(
    key: K,
    value: ShipmentSettingsType[K]
  ) => setSettings({ ...settings, [key]: value });
  const setSender = (
    key: keyof ShipmentSettingsType["sender"],
    value: string
  ) => set("sender", { ...settings.sender, [key]: value });
  const setNumber = (
    key:
      | "default_box_weight_grams"
      | "own_delivery_cost"
      | "weight_limit_kg"
      | "event_payload_days"
      | "event_retention_days"
      | "not_scanned_working_days",
    value: string
  ) => {
    const parsed = Number(value.replace(",", "."));
    set(
      key,
      (value.trim() === "" || !Number.isFinite(parsed) ? 0 : parsed) as never
    );
  };

  const printer = settings.label_printer;
  const pickedKey = printer
    ? printerKey(printer.host_name, printer.printer_name)
    : "";
  const pickPrinter = (key: string) => {
    const match = printers?.find(
      (p) => printerKey(p.hostName ?? "", p.printerName ?? "") === key
    );
    if (!match) return;
    set("label_printer", {
      host_name: match.hostName ?? "",
      printer_name: match.printerName ?? "",
      label_format: match.labelFormat || PRINT_FORMATS[0],
    });
  };
  const setPrinterField = (
    key: "host_name" | "printer_name" | "label_format",
    value: string
  ) => {
    const next = {
      host_name: printer?.host_name ?? "",
      printer_name: printer?.printer_name ?? "",
      label_format: printer?.label_format ?? PRINT_FORMATS[0],
      [key]: value,
    };
    set(
      "label_printer",
      next.host_name.trim() === "" && next.printer_name.trim() === ""
        ? null
        : next
    );
  };

  const save = () => {
    setSaving(true);
    apiSaveShipmentSettings(token, settings)
      .then((saved) => {
        setSettings(saved);
        addToast({ type: "success", message: "Shipment settings saved" });
      })
      .catch((error) =>
        addToast({ type: "error", message: errorText(error), keep: true })
      )
      .finally(() => setSaving(false));
  };

  const number = (
    key: Parameters<typeof setNumber>[0],
    label: string,
    unit?: string
  ) => (
    <TextField
      size="small"
      label={unit ? `${label} (${unit})` : label}
      value={String(settings[key])}
      disabled={disabled}
      onChange={(event) => setNumber(key, event.target.value)}
      inputProps={{ inputMode: "decimal" }}
      sx={{ width: 260 }}
    />
  );

  return (
    <ShipmentPage
      title="Shipment settings"
      actions={
        isManager && (
          <Button sx={{ color: "#fff" }} disabled={saving} onClick={save}>
            {saving ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Save"
            )}
          </Button>
        )
      }
    >
      {!isManager && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only managers can change the shipment settings.
        </Alert>
      )}

      <Section title="Label printer">
        <Box sx={{ width: "100%" }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.print_enabled}
                disabled={disabled}
                onChange={(event) => set("print_enabled", event.target.checked)}
              />
            }
            label="Print labels on the label printer after booking"
          />
          <Typography variant="body2" color="text.secondary">
            With printing off, the label is stored and downloaded from the
            shipment row. Switching on needs a printer.
          </Typography>
        </Box>
        <Box
          sx={{ display: "flex", gap: 2, alignItems: "center", width: "100%" }}
        >
          <Button
            variant="outlined"
            disabled={disabled || loadingPrinters}
            onClick={loadPrinters}
          >
            {loadingPrinters ? (
              <CircularProgress size={18} />
            ) : (
              "Load printers from Shipmondo"
            )}
          </Button>
          {printers && printers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 320 }}>
              <InputLabel>Printer</InputLabel>
              <Select
                label="Printer"
                value={
                  printers.some(
                    (p) =>
                      printerKey(p.hostName ?? "", p.printerName ?? "") ===
                      pickedKey
                  )
                    ? pickedKey
                    : ""
                }
                disabled={disabled}
                onChange={(event) => pickPrinter(event.target.value)}
              >
                {printers.map((p) => (
                  <MenuItem
                    key={printerKey(p.hostName ?? "", p.printerName ?? "")}
                    value={printerKey(p.hostName ?? "", p.printerName ?? "")}
                  >
                    {p.name || p.printerName} on {p.hostName}
                    {p.labelFormat ? ` (${p.labelFormat})` : ""}
                    {p.defaultPrinter ? ", default" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {printers && printers.length === 0 && (
            <Typography variant="body2">
              Shipmondo returned no printers. Is the print client running?
            </Typography>
          )}
          {printersError && <Alert severity="error">{printersError}</Alert>}
        </Box>
        <TextField
          size="small"
          label="Host name"
          value={printer?.host_name ?? ""}
          disabled={disabled}
          onChange={(event) => setPrinterField("host_name", event.target.value)}
          helperText="Filled by the picker; type only if the list is empty"
          sx={{ width: 260 }}
        />
        <TextField
          size="small"
          label="Printer name"
          value={printer?.printer_name ?? ""}
          disabled={disabled}
          onChange={(event) =>
            setPrinterField("printer_name", event.target.value)
          }
          sx={{ width: 260 }}
        />
        {printer && (
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>Printer format</InputLabel>
            {/* What the printer takes; a PDF job on a ZPL printer prints
                nothing and Shipmondo still answers success. */}
            <Select
              label="Printer format"
              value={
                PRINT_FORMATS.includes(printer.label_format as LabelFormat)
                  ? printer.label_format
                  : ""
              }
              disabled={disabled}
              onChange={(event) =>
                setPrinterField("label_format", event.target.value)
              }
            >
              {PRINT_FORMATS.map((format) => (
                <MenuItem key={format} value={format}>
                  {format}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>Label format</InputLabel>
          {/* The stored and downloaded label; the printer has its own. */}
          <Select
            label="Label format"
            value={settings.label_format}
            disabled={disabled}
            onChange={(event) =>
              set("label_format", event.target.value as LabelFormat)
            }
          >
            {LABEL_FORMATS.map((format) => (
              <MenuItem key={format} value={format}>
                {format}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Section>

      <Section title="Carriers">
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>Default carrier</InputLabel>
          <Select
            label="Default carrier"
            value={settings.default_carrier}
            disabled={disabled}
            onChange={(event) =>
              set("default_carrier", event.target.value as Carrier)
            }
          >
            {CARRIERS.map((carrier) => (
              <MenuItem key={carrier} value={carrier}>
                {CARRIER_LABELS[carrier]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {CARRIERS.map((carrier) => (
          <FormControlLabel
            key={carrier}
            control={
              <Switch
                checked={!!settings.own_agreement[carrier]}
                disabled={disabled}
                onChange={(event) =>
                  set("own_agreement", {
                    ...settings.own_agreement,
                    [carrier]: event.target.checked,
                  })
                }
              />
            }
            label={`${CARRIER_LABELS[carrier]}: book on our own agreement`}
          />
        ))}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ width: "100%" }}
        >
          Off books on Shipmondo's agreement. Switch on only once the carrier
          agreement is connected in Shipmondo.
        </Typography>
      </Section>

      <Section title="Weights and prices">
        {number("default_box_weight_grams", "Default box weight", "g")}
        {number("weight_limit_kg", "Price on request above", "kg")}
        {number("own_delivery_cost", "Default own-delivery cost", "kr ex VAT")}
      </Section>

      <Section title="Sender">
        {(
          [
            ["name", "Name"],
            ["attention", "Attention"],
            ["address1", "Address"],
            ["address2", "Address 2"],
            ["zipcode", "Postal code"],
            ["city", "City"],
            ["country_code", "Country code"],
            ["email", "Email"],
            ["mobile", "Phone"],
          ] as [keyof ShipmentSettingsType["sender"], string][]
        ).map(([key, label]) => (
          <TextField
            key={key}
            size="small"
            label={label}
            value={settings.sender[key] ?? ""}
            disabled={disabled}
            onChange={(event) => setSender(key, event.target.value)}
            sx={{ width: 260 }}
          />
        ))}
      </Section>

      <Section title="Notifications">
        <FormControlLabel
          control={
            <Switch
              checked={settings.notifications.email_nt}
              disabled={disabled}
              onChange={(event) =>
                set("notifications", {
                  ...settings.notifications,
                  email_nt: event.target.checked,
                })
              }
            />
          }
          label="Carrier email notification (EMAIL_NT)"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.notifications.sms_nt}
              disabled={disabled}
              onChange={(event) =>
                set("notifications", {
                  ...settings.notifications,
                  sms_nt: event.target.checked,
                })
              }
            />
          }
          label="Carrier SMS notification (SMS_NT)"
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ width: "100%" }}
        >
          Our own emails (sent, ready for pickup, handed to driver) go out
          either way. These add the carrier's own messages.
        </Typography>
      </Section>

      <Section title="Packing queue and checks">
        <FormControl size="small" sx={{ width: 260 }}>
          <InputLabel>Orders enter the queue</InputLabel>
          <Select
            label="Orders enter the queue"
            value={settings.queue_entry}
            disabled={disabled}
            onChange={(event) =>
              set(
                "queue_entry",
                event.target.value as ShipmentSettingsType["queue_entry"]
              )
            }
          >
            <MenuItem value="processing">At PROCESSING</MenuItem>
            <MenuItem value="production_done">
              When every line is ready
            </MenuItem>
          </Select>
        </FormControl>
        {number(
          "not_scanned_working_days",
          "Flag unscanned labels after",
          "working days"
        )}
        <FormControlLabel
          control={
            <Switch
              checked={settings.monitor_poll}
              disabled={disabled}
              onChange={(event) => set("monitor_poll", event.target.checked)}
            />
          }
          label="Poll Shipmondo for label states (fallback for missed webhooks)"
        />
        <Divider sx={{ width: "100%" }} />
        {number("event_payload_days", "Keep webhook payloads", "days")}
        {number("event_retention_days", "Keep webhook events", "days")}
      </Section>
    </ShipmentPage>
  );
};

export default ShipmentSettings;
